import { useState } from "react";

import { Check, Download, X as XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useBulkSetScreening,
  useManuscriptArchiveDownload,
} from "@/hooks/use-contest-submissions";
import type { ContestScreeningResult } from "@/types/contest-submission";

interface Props {
  selectedIds: string[];
  onClear: () => void;
}

const CONFIRM_COPY: Record<
  Exclude<ContestScreeningResult, "PENDING">,
  { title: string; action: string }
> = {
  PASS: { title: "일괄 적격 처리", action: "적격" },
  FAIL: { title: "일괄 부적격 처리", action: "부적격" },
};

/**
 * 체크박스 선택이 있을 때 표 위에 뜨는 일괄 판정 바.
 * 실수 방지를 위해 confirm 다이얼로그를 거친다. 개별 심사 메모는 유지된다.
 */
export function ContestSubmissionBulkBar({ selectedIds, onClear }: Props) {
  const [pendingResult, setPendingResult] = useState<"PASS" | "FAIL" | null>(
    null,
  );
  const bulkSet = useBulkSetScreening();
  const archive = useManuscriptArchiveDownload();

  if (selectedIds.length === 0) return null;

  const handleConfirm = async () => {
    if (!pendingResult) return;
    try {
      await bulkSet.mutateAsync({
        submissionIds: selectedIds,
        result: pendingResult,
      });
      setPendingResult(null);
      onClear();
    } catch {
      // 토스트는 훅에서 처리 — 다이얼로그는 열어두어 재시도 가능하게.
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 rounded-md border bg-muted/60 px-3 py-2">
        <span className="text-sm font-medium">{selectedIds.length}건 선택</span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="text-emerald-700"
            onClick={() => setPendingResult("PASS")}
          >
            <Check className="mr-1 h-3.5 w-3.5" />
            일괄 적격
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-rose-700"
            onClick={() => setPendingResult("FAIL")}
          >
            <XIcon className="mr-1 h-3.5 w-3.5" />
            일괄 부적격
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => archive.mutate({ submissionIds: selectedIds })}
            disabled={archive.isPending}
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            {archive.isPending ? "준비 중…" : "원고 다운로드"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear}>
            선택 해제
          </Button>
        </div>
      </div>

      <Dialog
        open={pendingResult !== null}
        onOpenChange={(open) => {
          if (!open) setPendingResult(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingResult ? CONFIRM_COPY[pendingResult].title : ""}
            </DialogTitle>
            <DialogDescription>
              선택한 {selectedIds.length}건을{" "}
              {pendingResult ? CONFIRM_COPY[pendingResult].action : ""} 처리합니다.
              개별 심사 메모는 유지되며, 판정은 다시 변경할 수 있습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingResult(null)}
              disabled={bulkSet.isPending}
            >
              취소
            </Button>
            <Button onClick={handleConfirm} disabled={bulkSet.isPending}>
              {bulkSet.isPending ? "처리 중…" : "확인"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
